/*
 * Copyright (c) 2025 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { BaseCallback } from './base-callback.js';
/**
 * Represents a callback used to collect attributes.
 *
 * @typeparam T Maps to StringAttributeInputCallback, NumberAttributeInputCallback and
 *     BooleanAttributeInputCallback, respectively
 */
export class AttributeInputCallback extends BaseCallback {
    payload;
    /**
     * @param payload The raw payload returned by OpenAM
     */
    constructor(payload) {
        super(payload);
        this.payload = payload;
    }
    /**
     * Gets the attribute name.
     */
    getName() {
        return this.getOutputByName('name', '');
    }
    /**
     * Gets the attribute prompt.
     */
    getPrompt() {
        return this.getOutputByName('prompt', '');
    }
    /**
     * Gets whether the attribute is required.
     */
    isRequired() {
        return this.getOutputByName('required', false);
    }
    /**
     * Gets the callback's failed policies.
     */
    getFailedPolicies() {
        const failedPoliciesJsonStrings = this.getOutputByName('failedPolicies', []);
        try {
            return failedPoliciesJsonStrings.map((v) => JSON.parse(v));
        }
        catch {
            throw new Error('Unable to parse "failed policies" from the ForgeRock server. The JSON within `AttributeInputCallback` was either malformed or missing.');
        }
    }
    /**
     * Gets the callback's applicable policies.
     */
    getPolicies() {
        return this.getOutputByName('policies', {});
    }
    /**
     * Set if validating value only.
     */
    setValidateOnly(value) {
        this.setInputValue(value, /validateOnly/);
    }
    /**
     * Sets the attribute's value.
     */
    setValue(value) {
        this.setInputValue(value);
    }
}
